import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useStores } from 'hooks/useStores';
import Seo from 'components/Seo';
import ArticleJsonLd from 'components/JsonLd/ArticleJsonLd';
import Breadcrumbs from 'components/Breadcrumbs';
import HtmlContent from 'utils/HtmlContent';
import styles from './article.module.scss';

const extractToc = (htmlString) => {
  if (!htmlString) return [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  return Array.from(doc.querySelectorAll('h2, h3')).map((h, i) => ({
    level: h.tagName.toLowerCase(),
    text: h.textContent,
    id: `heading-${i}`,
  }));
};

const injectTocIds = (htmlString) => {
  if (!htmlString) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  doc.querySelectorAll('h2, h3').forEach((h, i) => {
    h.id = `heading-${i}`;
  });
  return doc.body.innerHTML;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const ArticleDetail = () => {
  const { slug } = useParams();
  const { api } = useStores();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setData(null);

    api.content
      .getArticleBySlug(slug)
      .then((response) => {
        setData(response);
      })
      .catch(() => {
        setHasError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [api, slug]);

  if (isLoading) {
    return (
      <main className={styles.page}>
        <p className={styles.loading}>Загрузка...</p>
      </main>
    );
  }

  if (hasError || !data) {
    return (
      <main className={styles.page}>
        <p className={styles.error}>Статья не найдена</p>
      </main>
    );
  }

  const tocItems = data.toc_enabled ? extractToc(data.content) : [];
  const hasToc = tocItems.length > 0;
  const processedContent = hasToc
    ? injectTocIds(data.content)
    : data.content;

  return (
    <>
      <Seo seo={data.seo} pageImage={data.cover_image} />
      <ArticleJsonLd article={data} />

      <main className={styles.page}>
        <Breadcrumbs type="article" slug={slug} />

        <h1 className={styles.title}>{data.seo?.h1 || data.title}</h1>

        <div className={styles.meta}>
          {data.author_name && (
            <span className={styles.author}>{data.author_name}</span>
          )}
          {data.published_at && (
            <time className={styles.date} dateTime={data.published_at}>
              {formatDate(data.published_at)}
            </time>
          )}
        </div>

        {data.cover_image && (
          <img
            className={styles.cover}
            src={data.cover_image}
            alt={data.cover_image_alt || data.title}
            loading="eager"
          />
        )}

        {hasToc && (
          <nav className={styles.toc}>
            <h3 className={styles.tocTitle}>Содержание</h3>
            <ol className={styles.tocList}>
              {tocItems.map((item) => (
                <li
                  key={item.id}
                  className={
                    item.level === 'h3' ? styles.tocItemNested : styles.tocItem
                  }
                >
                  <a href={`#${item.id}`} className={styles.tocLink}>
                    {item.text}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <HtmlContent
          content={processedContent}
          className={styles.content}
        />
      </main>
    </>
  );
};

export default ArticleDetail;
